import { useReducer } from 'preact/hooks';
import { useWidgetConsent } from '../../consent/useWidgetConsent';
// Pages
import { ConsentForm } from '../base/ConsentForm/ConsentForm';
import { JourneyPlannerTab } from '../features/journey-planning/JourneyPlannerTab/JourneyPlannerTab';
import { LiveDeparturesTab } from '../features/departures/LiveDeparturesTab/LiveDeparturesTab';
import { TimetablesTab } from '../features/timetables/TimetablesTab/TimetablesTab';
import { Tab, Tabs, type TabsClassNames } from '../base/Tabs/Tabs';
import { Footer } from '../base/Footer/Footer';
import type { WidgetTabId } from '../../widgetConfig';
import type { TenantFooterLink } from '../../config/tenantConfig';
import {
  createDeparturesStopDetailsPage,
  createInitialJourneyWidgetPageState,
  createPlanLineTimetableDetailsPage,
  createPlanItineraryDetailsPage,
  createPlanStopDeparturesDetailsPage,
  createTimetableLineDetailsPage,
  createTimetableStopDeparturesDetailsPage,
  getCurrentJourneyWidgetPage,
  isJourneyWidgetActivityId,
  reduceJourneyWidgetPageState,
} from './pageState';
import { JourneyWidgetBackContext } from './JourneyWidgetBackContext';
import {
  shouldCapWidgetHeight,
  shouldShowFooter,
  shouldShowTopLevelTabs,
} from './pageUi';
import { createActivityPageNavigation } from './activityNavigation';
import { useJourneyWidgetTracking } from './useJourneyWidgetTracking';
// Styles
import styles from './Widget.module.css';

// These are consts as they can be used by integrators to pick the tab to open by default.
// They shouldn't change.
const JOURNEY_WIDGET_TAB_IDS = {
  PLAN: 'plan',
  DEPARTURES: 'departures',
  TIMETABLES: 'timetables',
} as const satisfies Record<string, WidgetTabId>;

export const JOURNEY_TABS_CLASS_NAMES: TabsClassNames = {
  root: styles.tabs,
  tabBar: styles.tabBar,
  tab: styles.tab,
  activeTab: styles.active,
  content: styles.content,
  panel: styles.panel,
};

interface JourneyWidgetTabsProps {
  initialTab?: WidgetTabId;
  initialOrigin?: string;
  initialDestination?: string;
  initialDepartureStopId?: string;
  footerLinks?: TenantFooterLink[];
}

/**
 * Coordinates top-level widget navigation.
 *
 * The widget keeps one independent page stack per activity tab. Switching tabs
 * only changes the active activity.
 * Search/results/details navigation changes that activity's own stack.
 *
 * A tab-scoped back action is provided by JourneyWidgetBackContext, so shared details-page UI can navigate back
 * without being tightly coupled to this reducer.
 */
export function JourneyWidgetTabs({
  initialTab = JOURNEY_WIDGET_TAB_IDS.PLAN,
  initialOrigin = '',
  initialDestination = '',
  initialDepartureStopId = '',
  footerLinks = [],
}: JourneyWidgetTabsProps) {
  const { consentState, setConsentState } = useWidgetConsent();
  const [pageState, dispatchPageState] = useReducer(
    reduceJourneyWidgetPageState,
    initialTab,
    createInitialJourneyWidgetPageState,
  );

  const planPage = getCurrentJourneyWidgetPage(
    pageState,
    JOURNEY_WIDGET_TAB_IDS.PLAN,
  );
  const departuresPage = getCurrentJourneyWidgetPage(
    pageState,
    JOURNEY_WIDGET_TAB_IDS.DEPARTURES,
  );
  const timetablesPage = getCurrentJourneyWidgetPage(
    pageState,
    JOURNEY_WIDGET_TAB_IDS.TIMETABLES,
  );
  const showTopLevelTabs = shouldShowTopLevelTabs(pageState);
  const showFooter = shouldShowFooter(pageState);
  const capHeight = shouldCapWidgetHeight(pageState);
  const shouldShowConsentForm = consentState === 'prompt';
  const activePage = getCurrentJourneyWidgetPage(
    pageState,
    pageState.activeActivity,
  );

  useJourneyWidgetTracking({
    pageState,
    activePage,
    initialTab,
    initialOrigin,
    initialDestination,
    initialDepartureStopId,
  });

  const onTabChange = (tabId: string) => {
    if (!isJourneyWidgetActivityId(tabId)) {
      return;
    }

    dispatchPageState({
      type: 'switch-activity',
      activityId: tabId,
    });
  };

  const planPageNavigation = createActivityPageNavigation(
    dispatchPageState,
    JOURNEY_WIDGET_TAB_IDS.PLAN,
  );
  const departuresPageNavigation = createActivityPageNavigation(
    dispatchPageState,
    JOURNEY_WIDGET_TAB_IDS.DEPARTURES,
  );
  const timetablesPageNavigation = createActivityPageNavigation(
    dispatchPageState,
    JOURNEY_WIDGET_TAB_IDS.TIMETABLES,
  );

  return (
    <div
      className={
        capHeight ? `${styles.container} ${styles.capped}` : styles.container
      }
    >
      <Tabs
        initialTab={initialTab}
        activeTab={pageState.activeActivity}
        onTabChange={onTabChange}
        showTabBar={showTopLevelTabs}
        classNames={JOURNEY_TABS_CLASS_NAMES}
      >
        <Tab
          id={JOURNEY_WIDGET_TAB_IDS.PLAN}
          label={'Plan your\nJourney'}
          ariaLabel="Plan your journey"
        >
          {() => (
            <JourneyWidgetBackContext.Provider
              value={planPageNavigation.goBack}
            >
              <JourneyPlannerTab
                currentPage={planPage}
                initialOrigin={initialOrigin}
                initialDestination={initialDestination}
                onShowResults={planPageNavigation.showResults}
                onShowItineraryDetails={(itineraryId) =>
                  planPageNavigation.showPage(
                    createPlanItineraryDetailsPage(itineraryId),
                  )
                }
                onShowStopDeparturesDetails={(options) =>
                  planPageNavigation.showPage(
                    createPlanStopDeparturesDetailsPage(options),
                  )
                }
                onShowLineTimetableDetails={(options) =>
                  planPageNavigation.showPage(
                    createPlanLineTimetableDetailsPage(options),
                  )
                }
              />
            </JourneyWidgetBackContext.Provider>
          )}
        </Tab>

        <Tab
          id={JOURNEY_WIDGET_TAB_IDS.DEPARTURES}
          label={'View Live\nDepartures'}
          ariaLabel="View Live Departures"
        >
          {() => (
            <JourneyWidgetBackContext.Provider
              value={departuresPageNavigation.goBack}
            >
              <LiveDeparturesTab
                currentPage={departuresPage}
                initialDepartureStopId={initialDepartureStopId}
                onShowResults={departuresPageNavigation.showResults}
                onShowDetails={(stopAtcoCode) =>
                  departuresPageNavigation.showPage(
                    createDeparturesStopDetailsPage(stopAtcoCode),
                  )
                }
              />
            </JourneyWidgetBackContext.Provider>
          )}
        </Tab>

        <Tab
          id={JOURNEY_WIDGET_TAB_IDS.TIMETABLES}
          label={'Find a\nTimetable'}
          ariaLabel="Find a Timetable"
        >
          {() => (
            <JourneyWidgetBackContext.Provider
              value={timetablesPageNavigation.goBack}
            >
              <TimetablesTab
                currentPage={timetablesPage}
                onShowResults={timetablesPageNavigation.showResults}
                onShowDetails={(lineId) =>
                  timetablesPageNavigation.showPage(
                    createTimetableLineDetailsPage(lineId),
                  )
                }
                onShowStopDeparturesDetails={(options) =>
                  timetablesPageNavigation.showPage(
                    createTimetableStopDeparturesDetailsPage(options),
                  )
                }
              />
            </JourneyWidgetBackContext.Provider>
          )}
        </Tab>
      </Tabs>

      {shouldShowConsentForm && (
        <div>
          <ConsentForm
            onAccept={() => setConsentState('granted')}
            onReject={() => setConsentState('denied')}
          />
        </div>
      )}

      {showFooter && <Footer links={footerLinks} />}
    </div>
  );
}
